import React, { useEffect, useRef, useState } from 'react';
import {
    Drawer, Spin, Tag, Space, Button, Typography, Descriptions, Image,
    Modal, Input, Tooltip, message, Alert, Radio, DatePicker, Card, Empty, Checkbox,
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined,
    QuestionCircleOutlined, FileImageOutlined, ExclamationCircleOutlined,
    CloudDownloadOutlined, SearchOutlined, SwapOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { fuelFormImageApi } from './api';
import ToolTraceViewer from '../FlightLogRecognition/ToolTraceViewer';
import { alreadyBoundMessage, isAlreadyBoundError, showCorrectionResultModal } from './correctionResult';
import { RE_EXTRACTABLE_STATUSES, RE_EXTRACT_CONFIRM_TEXT, RE_EXTRACT_QUOTA_NOTE } from './reExtractPolicy';

const { Text } = Typography;

// 与列表页一致：统一收敛为 5 档展示；低置信度/多候选的细节由下方 Alert 提示
const STATUS_TAG = {
    pending_extract: { color: 'blue', label: '识别中' },
    pending_match: { color: 'blue', label: '识别中' },
    pending_review: { color: 'gold', label: '待审核' },
    pending_review_low_confidence: { color: 'gold', label: '待审核' },
    pending_review_ambiguous_match: { color: 'gold', label: '待审核' },
    failed_permanent: { color: 'red', label: '识别失败' },
    confirmed: { color: 'green', label: '已确认' },
    rejected: { color: 'default', label: '已拒绝' },
};

const MATCH_STATUS_TAG = {
    auto_matched: { color: 'green', label: '自动匹配' },
    manual_matched: { color: 'cyan', label: '人工选定' },
    unmatched: { color: 'default', label: '未匹配' },
};

const FIELD_LABELS = {
    previous_fuel_kg: '加油前',
    out_fuel_kg: '加油后（OUT）',
    fuel_uplift_liters: '加油量 L',
    fuel_density: '密度',
    fuel_uplift_kg: '加油量 kg',
    aircraft_reg: '机号',
    flight_number: '航班号',
    flight_date: '日期',
    station: '站点',
};

// 展示顺序：首行 加油前 / 加油后（OUT）；次行 加油量 L / 密度 / 加油量 kg
const FUEL_FIELDS = [
    'previous_fuel_kg', 'out_fuel_kg', 'fuel_uplift_liters', 'fuel_density', 'fuel_uplift_kg',
];
// column=3 对齐网格：首行 加油前 / 加油后（OUT）/ 占位；次行三个字段各占 1
const FUEL_FIELD_SPAN = { previous_fuel_kg: 1, out_fuel_kg: 1 };
const FUEL_LABEL_STYLE = { whiteSpace: 'nowrap', width: 96 };

// 加油量 kg 与 升数×密度 偏差超过 max(50, 2% kg) 时返回估算 kg，否则 null（仅软提示，不阻止提交）
const fuelKgMismatch = (liters, density, kg) => {
    const toNum = (v) => (v == null || String(v).trim() === '' ? NaN : Number(v));
    const l = toNum(liters); const d = toNum(density); const k = toNum(kg);
    if (![l, d, k].every(Number.isFinite)) return null;
    const expected = l * d;
    return Math.abs(expected - k) > Math.max(50, Math.abs(k) * 0.02) ? Math.round(expected) : null;
};

// 油量覆盖值：去掉首尾空白后为空 = 清空（null）；非空但不是有限数字 = 非法，阻止提交
const isInvalidFuelInput = (v) => {
    if (v == null) return false;
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '') return false;
    return !Number.isFinite(Number(t));
};
const invalidFuelFields = (overrides) => FUEL_FIELDS.filter((f) => isInvalidFuelInput(overrides[f]));
const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');

// 图片上这一格是空的，但重算把数补进了报告。不是图片确认过的数，之后的邮件可以改。
const ESTIMATE_BLANK_HINT = {
    out_fuel_kg: '报告里按加油前加加油量暂估',
    fuel_uplift_kg: '报告里按升和密度暂估',
};

const estimatedBlankHint = (detail, field) => {
    const template = ESTIMATE_BLANK_HINT[field];
    if (!template) return '';
    const news = [];
    let estimated = false;
    (detail?.correction_result?.reports || []).forEach((report) => {
        if (!Array.isArray(report?.image_fields) || report.image_fields.includes(field)) return;
        const change = report.changes?.[field];
        if (!change) return;
        estimated = true;
        if (change.new != null && change.new !== '') news.push(String(change.new));
    });
    if (!estimated) return '';
    const unique = [...new Set(news)];
    const valueBit = unique.length === 1 ? `（${unique[0]}）` : '';
    return `${template}${valueBit}，之后的邮件可以改`;
};

const HEADER_FIELDS = ['flight_number', 'flight_date', 'aircraft_reg', 'station'];

const ConfidenceTag = ({ value, low }) => {
    if (value == null) return <Tag>未知</Tag>;
    const pct = (value * 100).toFixed(0) + '%';
    return <Tag color={low ? 'orange' : value > 0.85 ? 'green' : 'gold'}>{pct}</Tag>;
};

const isLowConfidenceField = (perFieldConfidence, field) => {
    const v = perFieldConfidence?.[field];
    if (v == null) return false;
    return v < 0.7;
};

const flightLabel = (f) => {
    if (!f) return '-';
    const route = (f.departure_airport || f.arrival_airport)
        ? ` · ${f.departure_airport || '?'}→${f.arrival_airport || '?'}` : '';
    return `${f.flight_number || '-'} · ${f.flight_date || '-'} · ${f.aircraft_reg || '无机号'}${route}`;
};

/** 识别日期是 YYYY-MM-DD，按日历日解析，避免被当成 UTC 午夜再转成本地日期。 */
const parseFlightDate = (value) => {
    if (!value) return null;
    const dateOnly = String(value).match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (!dateOnly) return null;
    const [year, month, day] = dateOnly.split('-').map(Number);
    const parsed = dayjs(new Date(year, month - 1, day));
    return parsed.isValid() ? parsed : null;
};

const searchQueryFromPrefill = (defaultQuery) => ({
    flight_number: defaultQuery?.flight_number || '',
    flight_date: parseFlightDate(defaultQuery?.flight_date),
    aircraft_reg: defaultQuery?.aircraft_reg || '',
    departure_airport: defaultQuery?.departure_airport || '',
});

const sameImageId = (a, b) => a != null && b != null && String(a) === String(b);

/** 候选 / 搜索结果的单选列表。 */
const FlightRadioList = ({ flights, value, onChange }) => (
    <Radio.Group
        value={value}
        onChange={(e) => {
            const id = e.target.value;
            onChange(id, flights.find((f) => f.flight_operation_id === id) || null);
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
        {flights.map((f) => (
            <Radio key={f.flight_operation_id} value={f.flight_operation_id}>
                <Space size={6} wrap>
                    <Text style={{ fontFamily: 'Courier New, monospace' }}>{flightLabel(f)}</Text>
                    {f.status && <Tag style={{ margin: 0 }}>{f.status}</Tag>}
                </Space>
            </Radio>
        ))}
    </Radio.Group>
);

/**
 * 航班搜索区：按航班号 / 日期 / 机号 / 始发站搜 FOP，选中一条作为绑定目标。
 * 打开当前单时用识别基础信息预填一次；之后改搜索条件不跟识别字段逐字同步。
 */
const FlightSearchPanel = ({ defaultQuery, selectedId, onSelect }) => {
    const [query, setQuery] = useState(() => searchQueryFromPrefill(defaultQuery));
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const searchSeq = useRef(0);

    const handleSearch = async () => {
        const params = {};
        if (query.flight_number.trim()) params.flight_number = query.flight_number.trim();
        if (query.flight_date) params.flight_date = query.flight_date.format('YYYY-MM-DD');
        if (query.aircraft_reg.trim()) params.aircraft_reg = query.aircraft_reg.trim();
        if (query.departure_airport.trim()) params.departure_airport = query.departure_airport.trim();
        if (!Object.keys(params).length) {
            message.warning('请至少填一个搜索条件');
            return;
        }
        const seq = ++searchSeq.current;
        setSearching(true);
        try {
            const res = await fuelFormImageApi.searchFlights(params);
            if (seq !== searchSeq.current) return;
            setResults((res.data || res)?.items || []);
        } catch (err) {
            if (seq !== searchSeq.current) return;
            message.error('搜索失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            if (seq === searchSeq.current) setSearching(false);
        }
    };

    return (
        <div>
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    size="small" placeholder="航班号" allowClear style={{ width: '20%' }}
                    value={query.flight_number} onPressEnter={handleSearch}
                    onChange={(e) => setQuery((q) => ({ ...q, flight_number: e.target.value }))}
                />
                <DatePicker
                    size="small" placeholder="航班日期(UTC)" style={{ width: '26%' }}
                    value={query.flight_date}
                    onChange={(v) => setQuery((q) => ({ ...q, flight_date: v }))}
                />
                <Input
                    size="small" placeholder="机号" allowClear style={{ width: '18%' }}
                    value={query.aircraft_reg} onPressEnter={handleSearch}
                    onChange={(e) => setQuery((q) => ({ ...q, aircraft_reg: e.target.value }))}
                />
                <Input
                    size="small" placeholder="始发站" allowClear style={{ width: '18%' }}
                    value={query.departure_airport} onPressEnter={handleSearch}
                    onChange={(e) => setQuery((q) => ({ ...q, departure_airport: e.target.value }))}
                />
                <Button size="small" type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
                    搜索
                </Button>
            </Space.Compact>
            {results !== null && (
                <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                    {results.length ? (
                        <FlightRadioList flights={results} value={selectedId} onChange={onSelect} />
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未找到航班，请调整条件" />
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * FuelFormImageReviewDrawer — 双栏审核 Drawer（加油单识别航班勾稽版）。
 *
 * - 左栏：图片预览（签名 URL）。
 * - 右栏：识别字段 + 航班核对（自动匹配结果 / 候选选择 / 手动搜索）
 *         + AI 查证过程 + 确认绑定 / 拒绝 / 改绑。
 */
const FuelFormImageReviewDrawer = ({ imageId, open, onClose, onChanged, hasEditPermission }) => {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [signedUrl, setSignedUrl] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState({});
    const [selectedFlightId, setSelectedFlightId] = useState(null);
    const [selectedFlightInfo, setSelectedFlightInfo] = useState(null);
    const [rebindMode, setRebindMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [notModelError, setNotModelError] = useState(false);
    const detailRequestSeq = useRef(0);

    const fetchDetail = async (requestedId) => {
        const targetId = requestedId || imageId;
        if (!targetId) return;
        const requestSeq = ++detailRequestSeq.current;
        setLoading(true);
        setSelectedFlightId(null);
        setSelectedFlightInfo(null);
        try {
            const [detailRes, urlRes] = await Promise.all([
                fuelFormImageApi.detail(targetId),
                fuelFormImageApi.signedUrl(targetId).catch(() => null),
            ]);
            if (requestSeq !== detailRequestSeq.current) return;
            const d = detailRes.data || detailRes;
            if (!sameImageId(d?.id, targetId)) return;
            setDetail(d);
            const u = urlRes?.data || urlRes;
            setSignedUrl(u?.signed_url || null);
            setFieldOverrides({});
            setNotModelError(false);
            setSelectedFlightId(d.flight_operation_id || null);
            setSelectedFlightInfo(d.flight_operation || null);
            setRebindMode(false);
        } catch (err) {
            if (requestSeq !== detailRequestSeq.current) return;
            message.error('加载详情失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            if (requestSeq === detailRequestSeq.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !imageId) return undefined;
        setDetail(null);
        setSignedUrl(null);
        setFieldOverrides({});
        setNotModelError(false);
        setSelectedFlightId(null);
        setSelectedFlightInfo(null);
        setRebindMode(false);
        fetchDetail(imageId);
        return () => { detailRequestSeq.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, imageId]);

    const handleSelectFlight = (id, info) => {
        setSelectedFlightId(id);
        if (info) setSelectedFlightInfo(info);
    };

    const handleConfirm = async () => {
        if (!detail || !sameImageId(detail.id, imageId)) return;
        if (!selectedFlightId) {
            message.warning('请先选择要绑定的航班');
            return;
        }
        const invalidFuel = invalidFuelFields(fieldOverrides);
        if (invalidFuel.length) {
            message.warning(`油量需为数字：${invalidFuel.map((f) => FIELD_LABELS[f] || f).join('、')}`);
            return;
        }
        setSubmitting(true);
        try {
            // 油量字段从 Input 出来是字符串，落库前转回数字（可转时）；
            // 空字符串视为清空，传 null（后端也会把 "" 写成 null），避免留下 ""
            const overrides = Object.fromEntries(
                Object.entries(fieldOverrides).map(([k, v]) => {
                    const trimmed = typeof v === 'string' ? v.trim() : v;
                    if (trimmed === '' || trimmed == null) return [k, null];
                    if (FUEL_FIELDS.includes(k)) return [k, Number(trimmed)];
                    return [k, trimmed];
                }),
            );
            const body = {
                flight_operation_id: selectedFlightId,
                field_overrides: Object.keys(overrides).length ? overrides : undefined,
                not_model_error: notModelError,
            };
            const res = await fuelFormImageApi.confirm(detail.id, body);
            const out = res.data || res;
            message.success('已确认绑定航班');
            // D14：展示本次对燃油报告的纠正结果（摘要不能代替再次打开时的油量主数字）
            if (out?.fuel_report_correction) {
                showCorrectionResultModal({ correction: out.fuel_report_correction });
            }
            onChanged?.();
            onClose?.();
        } catch (err) {
            if (isAlreadyBoundError(err)) {
                // D18：一 FOP 至多一张已确认图片；换图须先拒绝或改绑走旧图
                message.error(alreadyBoundMessage(err.response?.data?.detail));
            } else {
                message.error('确认失败: ' + (err.response?.data?.detail?.message || err.message));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // 已确认记录改绑到另一个航班（S3.4）
    const handleRebind = async () => {
        if (!detail || !selectedFlightId) {
            message.warning('请先选择新航班');
            return;
        }
        if (selectedFlightId === detail.flight_operation_id) {
            message.warning('选择的航班与当前绑定相同');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fuelFormImageApi.rebindFlight(detail.id, selectedFlightId);
            const out = res.data || res;
            message.success('已改绑航班');
            // D11：展示旧航班报告的回退结果与新航班报告的纠正结果
            if (out?.fuel_report_correction || (out?.fuel_report_revert?.reports || []).length) {
                showCorrectionResultModal({
                    title: '改绑完成：燃油报告回退与纠正',
                    correction: out.fuel_report_correction,
                    revert: out.fuel_report_revert,
                });
            }
            onChanged?.();
            await fetchDetail();
        } catch (err) {
            if (isAlreadyBoundError(err)) {
                // D18：目标航班已被其他已确认图片绑定，两侧报告均未改
                message.error(alreadyBoundMessage(err.response?.data?.detail));
            } else {
                message.error('改绑失败: ' + (err.response?.data?.detail?.message || err.message));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = () => {
        let reason = '';
        // D20：已确认图片也可拒绝；同一事务内回退本图对燃油报告的纠正
        const rejectingConfirmed = detail?.status === 'confirmed';
        Modal.confirm({
            title: '拒绝图片',
            icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    {rejectingConfirmed && (
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 8 }}
                            message="该图片已确认并可能已纠正燃油报告油量；拒绝将回退本图的纠正（之后被人工改过的字段保留当前值），并解除航班绑定。"
                        />
                    )}
                    <Input.TextArea rows={3} placeholder="请输入拒绝原因（必填）" onChange={(e) => { reason = e.target.value; }} />
                </div>
            ),
            okText: '确认拒绝', okButtonProps: { danger: true }, cancelText: '取消',
            onOk: async () => {
                if (!reason.trim()) {
                    message.warning('请输入拒绝原因');
                    return Promise.reject();
                }
                try {
                    const res = await fuelFormImageApi.reject(detail.id, reason.trim());
                    const out = res.data || res;
                    message.success('已拒绝');
                    if ((out?.fuel_report_revert?.reports || []).length) {
                        showCorrectionResultModal({ title: '拒绝成功：燃油报告回退结果', revert: out.fuel_report_revert });
                    }
                    onChanged?.();
                    onClose?.();
                } catch (err) {
                    // T15a：回退失败时后端整体回滚，这里必须显示为失败而非「已拒绝」
                    message.error('拒绝失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const handleRecover = async () => {
        try {
            await fuelFormImageApi.recover(detail.id);
            message.success('已恢复审核');
            onChanged?.();
            await fetchDetail();
        } catch (err) {
            message.error('恢复失败: ' + (err.response?.data?.detail?.message || err.message));
        }
    };

    // T44 / D9: 重新识别 — 四种未确认状态可用；「推倒重来」会清空当前未确认的修改，需二次确认（消耗 VLM 配额）
    const handleReExtract = () => {
        Modal.confirm({
            title: '重新识别',
            icon: <ExclamationCircleOutlined style={{ color: '#1677ff' }} />,
            content: (
                <div>
                    <div>{RE_EXTRACT_CONFIRM_TEXT}</div>
                    <Text type="secondary">{RE_EXTRACT_QUOTA_NOTE}</Text>
                </div>
            ),
            okText: '确定重新识别', cancelText: '取消',
            onOk: async () => {
                try {
                    await fuelFormImageApi.reExtract(detail.id);
                    // 清掉抽屉内未提交的本地修改、手选航班与勾选项，不残留半截状态
                    setFieldOverrides({});
                    setSelectedFlightId(null);
                    setSelectedFlightInfo(null);
                    setNotModelError(false);
                    message.success('已加入识别队列，请稍候刷新');
                    onChanged?.();
                    await fetchDetail();
                } catch (err) {
                    message.error('重新识别失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const isCurrent = Boolean(detail && imageId && sameImageId(detail.id, imageId));
    const ef = (isCurrent && detail.extracted_fields) || {};
    const rawVlm = (isCurrent && detail.raw_vlm_response) || {};
    const perFieldConfidence = rawVlm._per_field_confidence;
    const toolTrace = rawVlm._tool_trace;
    const modelAdjustments = rawVlm._model_adjustments || {};
    const blockedFields = Array.isArray(rawVlm._blocked_fields) ? rawVlm._blocked_fields : [];

    const candidates = (isCurrent && detail.flight_match_candidates?.candidates) || [];
    const isConfirmed = isCurrent && detail.status === 'confirmed';

    const reviewable = hasEditPermission && isCurrent && [
        'pending_review', 'pending_review_low_confidence', 'pending_review_ambiguous_match',
    ].includes(detail.status);

    // D20：已确认图片也可拒绝（会回退其对燃油报告的纠正）
    const canReject = hasEditPermission && isCurrent && [
        'pending_review', 'pending_review_low_confidence', 'pending_review_ambiguous_match', 'failed_permanent', 'confirmed',
    ].includes(detail.status);

    const canRecover = hasEditPermission && isCurrent && detail.status === 'rejected';
    const canReExtract = hasEditPermission && isCurrent && RE_EXTRACTABLE_STATUSES.includes(detail.status);

    // D25：已确认图片再次打开时，主数字 = 审核最终值（reviewed_fields）；
    // 没有最终值的存量已确认图片继续显示 extracted_fields；待审核态仍用识别结果 + 当场覆盖编辑
    const reviewedFields = (isCurrent && detail.reviewed_fields) || null;
    const fieldSource = (isConfirmed && reviewedFields) ? reviewedFields : ef;

    const headerValue = (field) => fieldOverrides[field] ?? fieldSource[field];

    // 已确认态：审核员清空的油量字段 reviewed_fields 为 null，但后端约定 null 不覆盖报告，
    // 报告仍保留原值 —— 不能只显示 '-' 让人以为报告也被清空了
    const correctionStatus = detail?.correction_result?.status;
    const renderReadOnlyValue = (field, value) => {
        if (!isBlank(value)) return <Text>{value}</Text>;
        if (isConfirmed && reviewedFields && FUEL_FIELDS.includes(field)) {
            const recognized = ef?.[field];
            const recognizedHint = isBlank(recognized) ? '' : `；识别值 ${recognized}`;
            const estimateHint = estimatedBlankHint(detail, field);
            const reportHint = correctionStatus === 'skipped_no_report'
                ? '该航班暂无燃油报告'
                : (estimateHint || '不修改报告，报告保留原值');
            return <Text type="secondary">未填（{reportHint}{recognizedHint}）</Text>;
        }
        return <Text>-</Text>;
    };

    // D7 / D14：模型自修正的轻量提示 + 一键恢复初读值；被反编造拦截的字段在字段下方展示「AI 修正被拦截（已保留初读值）」
    const renderAdjustmentHint = (field) => {
        if (reviewable && blockedFields.includes(field)) {
            return (
                <div style={{ marginTop: 4 }}>
                    <Tooltip title="模型声称的工具修正未在核对工具结果中命中，已自动保留初读值，请对照原图确认">
                        <Tag color="warning" style={{ margin: 0 }}>
                            AI 修正被拦截（已保留初读值）
                        </Tag>
                    </Tooltip>
                </div>
            );
        }
        const adj = modelAdjustments[field];
        if (!adj || !reviewable) return null;
        const rawRead = adj.raw_read;
        const restored = fieldOverrides[field] !== undefined && String(fieldOverrides[field]).trim() === String(rawRead ?? '').trim();
        return (
            <div style={{ marginTop: 4 }}>
                <Space size={4} wrap>
                    <Tooltip title={adj.reason || '模型依据工具核对结果修正了形近字迹'}>
                        <Tag color={restored ? 'default' : 'purple'} style={{ margin: 0 }}>
                            AI 修正：{String(rawRead ?? '空')} → {String(adj.final ?? '空')}
                        </Tag>
                    </Tooltip>
                    {restored ? (
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setFieldOverrides((prev) => {
                            const next = { ...prev };
                            delete next[field];
                            return next;
                        })}>
                            已恢复初读值，点击撤销
                        </Button>
                    ) : (
                        <Button type="link" size="small" style={{ padding: 0 }}
                            onClick={() => setFieldOverrides((prev) => ({ ...prev, [field]: rawRead ?? '' }))}>
                            恢复初读值
                        </Button>
                    )}
                </Space>
            </div>
        );
    };

    const renderHeaderField = (field, span, labelStyle) => {
        const value = headerValue(field);
        const lowConf = isLowConfidenceField(perFieldConfidence, field);
        return (
            <Descriptions.Item label={FIELD_LABELS[field] || field} key={field}
                span={span}
                labelStyle={labelStyle}
                contentStyle={lowConf ? { background: '#fff7e6', padding: '4px 8px', borderRadius: 4 } : undefined}>
                {reviewable ? (
                    <>
                        <Input
                            size="small"
                            style={{ width: '100%' }}
                            value={value ?? ''}
                            status={FUEL_FIELDS.includes(field) && isInvalidFuelInput(fieldOverrides[field]) ? 'error' : undefined}
                            onChange={(e) => setFieldOverrides({ ...fieldOverrides, [field]: e.target.value })}
                            suffix={lowConf ? <Tooltip title="该字段置信度偏低，请重点核对"><QuestionCircleOutlined style={{ color: '#fa8c16' }} /></Tooltip> : null}
                        />
                        {renderAdjustmentHint(field)}
                    </>
                ) : (
                    renderReadOnlyValue(field, value)
                )}
            </Descriptions.Item>
        );
    };

    const fuelKgExpected = fuelKgMismatch(
        headerValue('fuel_uplift_liters'), headerValue('fuel_density'), headerValue('fuel_uplift_kg'),
    );

    // 识别值 vs 绑定航班关键信息不一致时提示（机号 / 航班号 / 日期）
    // 比较前去掉空白/连字符并大写，避免 CT166 vs ct166、9HWFF vs 9H-WFF 误报
    const normFlightNo = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();
    const normReg = (v) => String(v || '').replace(/[\s-]+/g, '').toUpperCase();
    const flightForCompare = selectedFlightInfo;
    const mismatches = [];
    if (flightForCompare && isCurrent) {
        const shownNumber = headerValue('flight_number');
        const shownDate = headerValue('flight_date');
        const shownReg = headerValue('aircraft_reg');
        if (shownNumber && flightForCompare.flight_number
            && normFlightNo(shownNumber) !== normFlightNo(flightForCompare.flight_number)) {
            mismatches.push(`航班号 ${shownNumber} ≠ ${flightForCompare.flight_number}`);
        }
        if (shownDate && flightForCompare.flight_date && shownDate !== flightForCompare.flight_date) {
            mismatches.push(`日期 ${shownDate} ≠ ${flightForCompare.flight_date}`);
        }
        if (shownReg && flightForCompare.aircraft_reg
            && normReg(shownReg) !== normReg(flightForCompare.aircraft_reg)) {
            mismatches.push(`机号 ${shownReg} ≠ ${flightForCompare.aircraft_reg}`);
        }
    }

    const tagInfo = STATUS_TAG[detail?.status] || { color: 'default', label: detail?.status };
    const matchTag = MATCH_STATUS_TAG[detail?.flight_match_status] || MATCH_STATUS_TAG.unmatched;

    const renderFlightCard = () => {
        if (!detail) return null;
        const boundFlight = detail.flight_operation;
        return (
            <Card
                size="small"
                style={{ marginBottom: 16 }}
                title={(
                    <Space size={6}>
                        <SendOutlined />
                        <span>航班核对</span>
                        <Tag color={matchTag.color} style={{ margin: 0 }}>{matchTag.label}</Tag>
                    </Space>
                )}
                extra={isConfirmed && hasEditPermission && (
                    <Button size="small" icon={<SwapOutlined />} onClick={() => {
                        setRebindMode((v) => !v);
                        setSelectedFlightId(detail.flight_operation_id || null);
                        setSelectedFlightInfo(detail.flight_operation || null);
                    }}>
                        {rebindMode ? '取消改绑' : '改绑航班'}
                    </Button>
                )}
            >
                {/* 当前绑定 / 自动匹配结果 */}
                {boundFlight ? (
                    <Alert
                        type={boundFlight.deleted ? 'error' : 'success'}
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={(
                            <Space size={6} wrap>
                                <Text strong>{isConfirmed ? '已绑定：' : '建议绑定：'}</Text>
                                <Text style={{ fontFamily: 'Courier New, monospace' }}>{flightLabel(boundFlight)}</Text>
                                {boundFlight.status && <Tag style={{ margin: 0 }}>{boundFlight.status}</Tag>}
                                {boundFlight.deleted && <Tag color="red" style={{ margin: 0 }}>航班已删除</Tag>}
                            </Space>
                        )}
                    />
                ) : (
                    !candidates.length && (
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 8 }}
                            message="未自动匹配到航班，请用下方搜索手动选定"
                        />
                    )
                )}

                {/* 选中目标与识别字段不一致提示（不阻断） */}
                {mismatches.length > 0 && (reviewable || rebindMode) && (
                    <Alert
                        type="warning" showIcon style={{ marginBottom: 8 }}
                        message={`选中航班与识别结果不一致：${mismatches.join('；')}`}
                    />
                )}

                {/* 多候选选择（识别后匹配歧义） */}
                {reviewable && candidates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            候选航班（{candidates.length}），请选择：
                        </Text>
                        <FlightRadioList
                            flights={candidates}
                            value={selectedFlightId}
                            onChange={handleSelectFlight}
                        />
                    </div>
                )}

                {/* 手动搜索：待审核态始终可用；已确认态进入改绑模式后可用 */}
                {(reviewable || (isConfirmed && rebindMode)) && (
                    <div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            手动搜索航班（候选不对 / 未匹配时使用）：
                        </Text>
                        <FlightSearchPanel
                            key={imageId}
                            defaultQuery={{
                                flight_number: headerValue('flight_number'),
                                flight_date: headerValue('flight_date'),
                                aircraft_reg: headerValue('aircraft_reg'),
                                departure_airport: headerValue('station'),
                            }}
                            selectedId={selectedFlightId}
                            onSelect={handleSelectFlight}
                        />
                    </div>
                )}

                {/* 改绑提交 */}
                {isConfirmed && rebindMode && (
                    <div style={{ marginTop: 10, textAlign: 'right' }}>
                        <Button
                            type="primary" size="small" icon={<SwapOutlined />}
                            loading={submitting}
                            disabled={!selectedFlightId || selectedFlightId === detail.flight_operation_id}
                            onClick={handleRebind}
                        >
                            改绑到选中航班
                        </Button>
                    </div>
                )}
            </Card>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={1180}
            destroyOnHidden
            title={
                <Space>
                    <FileImageOutlined />
                    <Text strong>{isCurrent ? (detail.file_name || '加油单图片审核') : '加油单图片审核'}</Text>
                    {isCurrent && <Tag color={tagInfo.color}>{tagInfo.label}</Tag>}
                    {isCurrent && (
                        <ConfidenceTag
                            value={detail.extraction_confidence}
                            low={detail.status === 'pending_review_low_confidence'}
                        />
                    )}
                </Space>
            }
            footer={
                <Space>
                    {canRecover && <Button icon={<ReloadOutlined />} onClick={handleRecover}>恢复审核</Button>}
                    {canReExtract && (
                        <Button type="primary" ghost icon={<ReloadOutlined />} onClick={handleReExtract}>
                            重新识别
                        </Button>
                    )}
                    {canReject && <Button danger icon={<CloseCircleOutlined />} onClick={handleReject}>拒绝</Button>}
                    {reviewable && (
                        <Tooltip title="勾选后，本次对识别结果的修改仅作审计留痕：不计入 AI 识别纠偏率，也不会进入同机号纠错提示与规则总结">
                            <Checkbox checked={notModelError} onChange={(e) => setNotModelError(e.target.checked)}>
                                原单据本身写错（非 AI 看错）
                            </Checkbox>
                        </Tooltip>
                    )}
                    {reviewable && (
                        <Tooltip title={selectedFlightId ? '' : '需要先选定一个航班'}>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={submitting}
                                disabled={!selectedFlightId}
                                onClick={handleConfirm}
                            >
                                确认绑定航班
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            }
        >
            <Spin spinning={loading || (open && !!imageId && !isCurrent)}>
                {isCurrent && (
                    <div style={{ display: 'grid', gridTemplateColumns: '480px 1fr', gap: 24 }}>
                        {/* 左栏：图片预览 */}
                        <div>
                            {signedUrl ? (
                                <Image src={signedUrl} alt={detail.file_name}
                                    style={{ borderRadius: 6, maxHeight: 720, objectFit: 'contain' }} />
                            ) : (
                                <Alert type="warning" showIcon message="无法加载图片" />
                            )}
                            <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
                                {detail.file_name} · {(detail.file_size_bytes / 1024).toFixed(1)} KB
                            </div>
                        </div>

                        {/* 右栏：识别字段 + 航班核对 + AI 查证过程 */}
                        <div>
                            {detail.source_type === 'external_crawl' && (
                                <Alert
                                    style={{ marginBottom: 12 }}
                                    type="info"
                                    showIcon
                                    icon={<CloudDownloadOutlined />}
                                    message={
                                        detail.external_crawl_file?.deleted ? (
                                            <Space size={6}>
                                                <Text>来源：自动抓取</Text>
                                                <Text type="secondary">（原抓取记录已删除）</Text>
                                            </Space>
                                        ) : (
                                            <Space size={6} wrap>
                                                <Text>
                                                    来源：自动抓取
                                                    {detail.external_crawl_file
                                                        ? `（${detail.external_crawl_file.site_key || '-'} / ${detail.external_crawl_file.date_key || '-'}）`
                                                        : ''}
                                                </Text>
                                                {detail.external_crawl_file && (
                                                    <Link
                                                        to={`/settlement/external-crawl-config?config_id=${detail.external_crawl_file.url_config_id}&file_id=${detail.external_crawl_file.id}`}
                                                    >
                                                        查看抓取记录
                                                    </Link>
                                                )}
                                            </Space>
                                        )
                                    }
                                />
                            )}
                            {detail.status === 'failed_permanent' && (
                                <Alert
                                    style={{ marginBottom: 12 }}
                                    type="error"
                                    showIcon
                                    message="识别失败"
                                    description={
                                        (detail.raw_vlm_response?.error_message
                                            || '识别已重试 3 次仍未通过')
                                        + ' — 可点击「重新识别」让 AI 再试一次，或「拒绝」并重新上传更清晰的图片。'
                                    }
                                />
                            )}
                            {detail.status === 'pending_review_low_confidence' && (
                                <Alert style={{ marginBottom: 12 }} type="warning" showIcon
                                    message="整体置信度低于阈值或关键字段缺失，请仔细核对高亮字段"
                                    description={blockedFields.length > 0
                                        ? `AI 声称的工具修正未通过核验，已退回初读值：${blockedFields.map((f) => FIELD_LABELS[f] || f).join('、')}，请对照原图确认。`
                                        : undefined} />
                            )}
                            {detail.status === 'pending_review_ambiguous_match' && (
                                <Alert style={{ marginBottom: 12 }} type="warning" showIcon
                                    message={`匹配到 ${candidates.length} 个候选航班，请在下方选择正确的航班`} />
                            )}

                            <Descriptions size="small" column={2} bordered title="识别基础信息" style={{ marginBottom: 16 }}>
                                {HEADER_FIELDS.map((field) => renderHeaderField(field))}
                            </Descriptions>

                            {renderFlightCard()}

                            <Descriptions size="small" column={3} bordered title="识别油量字段"
                                style={{ marginBottom: fuelKgExpected != null ? 8 : 16 }}>
                                {FUEL_FIELDS.slice(0, 2).map((field) => renderHeaderField(field, FUEL_FIELD_SPAN[field], FUEL_LABEL_STYLE))}
                                <Descriptions.Item key="__fuel_placeholder" label="" labelStyle={FUEL_LABEL_STYLE}>{null}</Descriptions.Item>
                                {FUEL_FIELDS.slice(2).map((field) => renderHeaderField(field, 1, FUEL_LABEL_STYLE))}
                            </Descriptions>
                            {fuelKgExpected != null && (
                                <Text type="warning" style={{ display: 'block', marginBottom: 16 }}>
                                    加油量 kg 与 升数×密度（约 {fuelKgExpected} kg）不一致，请核对
                                </Text>
                            )}

                            {Array.isArray(toolTrace) && toolTrace.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <ToolTraceViewer trace={toolTrace} />
                                </div>
                            )}

                            {detail.review_notes && (
                                <Alert
                                    style={{ marginTop: 4 }}
                                    type="info"
                                    message="审核备注"
                                    description={detail.review_notes}
                                />
                            )}
                        </div>
                    </div>
                )}
            </Spin>
        </Drawer>
    );
};

export default FuelFormImageReviewDrawer;
